import { Box, Stack, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { RHFAutocomplete } from 'src/components/minimals/form';
import { useResponsive } from 'src/hooks/useResponsive';
import { TagResponse } from 'src/types/tags';

type Props = {
  name: string;
  tags: TagResponse[];
  onChange: (event: any, newValue: any) => void;
  error?: boolean;
  helperText?: string;
  label: string;
  size?: 'medium' | 'small';
  customListboxProps?: any;
  areLabelsSeparated?: boolean;
  selectedTagId?: string;
  labelRows?: boolean;
  isAssetController?: boolean;
  disabled?: boolean;
  /** Shown on hover only while `disabled`, to explain why the field is locked. */
  disabledTooltipText?: string;
};

export const SelectTagField = ({
  name,
  tags,
  onChange,
  error,
  helperText,
  label,
  size = 'medium',
  customListboxProps,
  areLabelsSeparated,
  selectedTagId,
  labelRows,
  isAssetController,
  disabled,
  disabledTooltipText,
}: Props) => {
  const theme = useTheme();
  const isMobile = useResponsive('down', 'sm');
  const [selectedTag, setSelectedTag] = useState<TagResponse>();

  useEffect(() => {
    if (tags && name) {
      const initialTag = tags.find((tag) => tag.id === selectedTagId);
      setSelectedTag(initialTag);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags, name]);

  const renderTagOptions = (props: any, option: string) => {
    const tag = tags.find((tag) => tag.id === option);

    return (
      <Stack
        {...props}
        key={tag?.id}
        data-testid={'dropdown-item'}
        sx={{
          flex: 1,
          flexDirection: 'row',
          height: '50px',
          justifyContent:
            tag && tag.displayName !== '' ? 'space-between!important' : 'flex-end!important',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontWeight: 700,
          }}
        >
          {tag?.displayName}
        </Typography>
        {renderAdditionalLabels(tag)}
      </Stack>
    );
  };

  const renderAdditionalLabels = (tag: any) => (
    <Stack
      sx={{
        flexDirection: labelRows ? 'row' : 'column',
        gap: '5px',
        marginTop: areLabelsSeparated ? '5px' : 0,
      }}
    >
      {tag?.gatewayName ? (
        <Stack
          sx={{
            background: theme?.palette?.info?.lighter,
            borderRadius: '10px',
            padding: areLabelsSeparated ? '1px 6px' : '1px 8px',
            justifyContent: 'center!important',
            alignItems: 'center!important',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontWeight: 700,
              fontSize: '10px',
            }}
            color={theme?.palette?.info?.dark}
          >
            {tag?.gatewayName}
          </Typography>
        </Stack>
      ) : null}
      {tag?.connectedAsset ? (
        <Stack
          sx={{
            background: theme.palette.grey[300],
            borderRadius: '10px',
            padding: areLabelsSeparated ? '1px 6px' : '1px 8px',
            justifyContent: 'center!important',
            alignItems: 'center!important',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: '10px',
              fontWeight: 700,
            }}
            color={theme.palette.grey[700]}
          >
            {tag?.connectedAsset}
          </Typography>
        </Stack>
      ) : null}
    </Stack>
  );
  const tagField = (
    <RHFAutocomplete
      disabled={disabled}
      customListboxProps={{
        ...customListboxProps,
        className: 'custom-autocomplete-scrollbar',
        style: {
          display: isMobile ? 'block' : customListboxProps?.style?.display,
          width: isMobile ? 'max-content' : customListboxProps?.style?.width,
          overflowX: isMobile ? 'auto' : customListboxProps?.style?.overflowX,
        },
      }}
      name={name}
      renderOption={renderTagOptions}
      options={['', ...tags.map((tag: any) => tag.id)]}
      isOptionEqualToValue={(option, value) => option === value}
      getOptionLabel={(option: string) => {
        const values = ['', ...tags.map((tag) => tag.id)];
        const labels = ['', ...tags.map((tag) => tag.displayName)];
        const index = values.indexOf(option);
        return `${labels[index]}`;
      }}
      onChange={(event, newValue) => {
        onChange(event, newValue);

        if (newValue) {
          const tag = tags.find((tag) => tag.id === newValue);
          setSelectedTag(tag);
        } else {
          setSelectedTag(undefined);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          error={error}
          helperText={helperText}
          inputProps={{
            ...params.inputProps,
            'data-sm': 'select-tag-input',
          }}
          label={label}
          size={size}
          InputProps={{
            ...params.InputProps,

            endAdornment: !areLabelsSeparated ? (
              <>
                {!isMobile && isAssetController && renderAdditionalLabels(selectedTag)}
                {params.InputProps.endAdornment}
              </>
            ) : (
              params.InputProps.endAdornment
            ),
          }}
        />
      )}
    />
  );

  return (
    <>
      {disabled && disabledTooltipText ? (
        <Tooltip
          placement="top-start"
          title={
            <Typography sx={{ maxWidth: 500, whiteSpace: 'normal' }}>
              {disabledTooltipText}
            </Typography>
          }
        >
          {/* A disabled input emits no hover events, so the tooltip needs an enabled wrapper. */}
          <Box>{tagField}</Box>
        </Tooltip>
      ) : (
        tagField
      )}
      {areLabelsSeparated ? renderAdditionalLabels(selectedTag) : null}
    </>
  );
};
